import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { UploadStatus, Icon, CircularProgress, Skeleton } from "@ann/web-components";
import { 
  TrainProgressContainer, 
  ContentArea, 
  UploadStatusContainer,
  NoDataWrapper,
  NoDataTitle,
  NoDataDescription
} from "./TrainProgress.styled";
import { Filters } from "./components";
import { useUploadFiles } from "../../hooks/useUploadFiles";

const TrainProgress = observer(() => {
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [orderDirection, setOrderDirection] = useState("asc");
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isFiltering, setIsFiltering] = useState(false);
  const filteringTimeoutRef = useRef(null);

  const { uploadFiles, documentsStore } = useUploadFiles();

  const handleToggleExpand = useCallback((fileId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  }, []);

  const handleRetry = useCallback(async (file) => {
    if (!file.file || !file.retryOptions) {
      console.warn('No file or retry options available for retry');
      return;
    }

    const identifier = file.id;
    documentsStore.removeTemporaryDocument(identifier);

    try {
      await documentsStore.uploadMultipleFiles([file.file], file.retryOptions);
    } catch (error) {
      console.error('Error retrying upload:', error);
    }
  }, [documentsStore]);

  // Memoize filtered and sorted files to avoid recalculation on every render
  const filteredUploadFiles = useMemo(() => {
    if (!uploadFiles.length) return [];
    
    const lowerSearchKeyword = searchKeyword.toLowerCase();
    const hasSearch = searchKeyword.length > 0;
    const hasStatusFilter = selectedStatus !== null;
    
    // First filter the files
    let filtered = uploadFiles.filter((file) => {
      // Search filter - search by file name
      if (hasSearch && !file.name.toLowerCase().includes(lowerSearchKeyword)) {
        return false;
      }
      
      // Status filter - single value (not array)
      if (hasStatusFilter && file.status !== selectedStatus) {
        return false;
      }
      
      return true;
    });

    // Then sort the filtered files
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (orderBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
          });
          break;
        case 'uploaded':
          comparison = a.uploadedAt.getTime() - b.uploadedAt.getTime();
          break;
        case 'progress':
          comparison = a.progress - b.progress;
          break;
        default:
          comparison = 0;
      }

      // Apply direction (asc or desc)
      return orderDirection === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }, [uploadFiles, searchKeyword, selectedStatus, orderBy, orderDirection]);

  // Handle filtering state when search, status, or sorting changes
  useEffect(() => {
    // Clear existing timeout
    if (filteringTimeoutRef.current) {
      clearTimeout(filteringTimeoutRef.current);
    }

    // Set filtering to true immediately
    setIsFiltering(true);

    // Simulate filtering delay (300ms)
    filteringTimeoutRef.current = setTimeout(() => {
      setIsFiltering(false);
      filteringTimeoutRef.current = null;
    }, 300);

    // Cleanup on unmount
    return () => {
      if (filteringTimeoutRef.current) {
        clearTimeout(filteringTimeoutRef.current);
      }
    };
  }, [searchKeyword, selectedStatus, orderBy, orderDirection]);

  // Memoize handlers to avoid recreation
  const handleSearchChange = useCallback((value) => {
    setSearchKeyword(value);
  }, []);

  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
  }, []);

  const handleSortChange = useCallback((value) => {
    setOrderBy(value);
  }, []);

  const handleOrderChange = useCallback(() => {
    // Toggle between 'asc' and 'desc' using current state
    setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  return (
    <TrainProgressContainer>
      <Filters
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={orderBy}
        orderDirection={orderDirection}
        loading={loading}
      />
      <ContentArea>
        {loading ? (
          <CircularProgress size="xl" />
        ) : isFiltering ? (
          <UploadStatusContainer>
            {[1, 2, 3].map((index) => (
              <Skeleton
                key={`skeleton-${index}`}
                height="100px"
                width="100%"
              />
            ))}
          </UploadStatusContainer>
        ) : (
          <>
            {filteredUploadFiles.length > 0 ? (
              <UploadStatusContainer>
                {filteredUploadFiles.map((file) => (
                  <UploadStatus
                    key={file.id}
                    name={file.name}
                    onToggleExpand={() => handleToggleExpand(file.id)}
                    progress={file.progress}
                    status={file.status}
                    statusMessage={file.statusMessage}
                    uploadedAt={file.uploadedAt}
                    lastUpdatedAt={file.lastUpdatedAt}
                    defaultExpanded={file.status === 'done' || expandedItems.has(file.id)}
                  />
                ))}
              </UploadStatusContainer>
            ) : (
              <NoDataWrapper>
                <Icon name="noDataAvailable" width="201px" height="195px" />
                <NoDataTitle>No uploads in progress</NoDataTitle>
                <NoDataDescription>
                  Upload files to see their training progress here.
                </NoDataDescription>
              </NoDataWrapper>
            )}
          </>
        )}
      </ContentArea>
    </TrainProgressContainer>
  );
});

TrainProgress.displayName = 'TrainProgress';

export default TrainProgress;
